import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Location, SortDirection, FormStatus } from '@/types';
import { 
    getLocationDoc, 
    getLocationDocs, 
    addLocationDoc, 
    setLocationDoc, 
    deleteLocationDoc,
    restoreLocationDoc
} from '@/services/firebaseApi';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';

export enum LocationsSortBy {
    NAME = 'name',
}

interface UseLocationsProps {
    storageId?: string;
    locationId?: string;
}

const useLocations = ({ storageId, locationId }: UseLocationsProps) => {
    const queryClient = useQueryClient();
    
    const [sortBy, setSortBy] = useState(LocationsSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

    const {
        data: allLocations,
        isLoading: locationsLoading,
        error: locationsError,
        refetch: refetchLocations
    } = useQuery({
        queryKey: ['locations', storageId, sortBy, sortDirection, displayDeleted],
        queryFn: () => getLocationDocs({ 
            storageId: storageId!,
            sortBy,
            sortDirection,
            includeDeleted: displayDeleted
        }),
        enabled: !!storageId,
        staleTime: 5 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
    });

    const {
        data: location,
        isLoading: locationLoading,
        error: locationError,
        refetch: refetchLocation
    } = useQuery({
        queryKey: ['location', storageId, locationId],
        queryFn: () => getLocationDoc(storageId!, locationId!),
        enabled: !!(storageId && locationId),
        staleTime: 5 * 60 * 1000,
    });

    const filteredLocations = useMemo(() => {
        if (!allLocations) return [];
        
        let filtered = [...allLocations];

        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((item) =>
                item.name.toLowerCase().includes(lower) ||
                (item.description && item.description.toLowerCase().includes(lower)) ||
                (item.notes && item.notes.toLowerCase().includes(lower))
            );
        }

        return filtered;
    }, [allLocations, filterQuery]);

    const createLocationMutation = useMutation({
        mutationFn: addLocationDoc,
        onSuccess: (result, variables) => {
            trackCreate('location');
            logger.info('Location created successfully');
            queryClient.invalidateQueries({ queryKey: ['locations', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storage', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
            queryClient.removeQueries({
                queryKey: ['storage', variables.ids.storageId],
                exact: true,
            });
        },
        onError: (error) => {
            logger.error('Failed to create location:', error);
        }
    });

    const updateLocationMutation = useMutation({
        mutationFn: setLocationDoc,
        onSuccess: (result, variables) => {
            trackEdit('location');
            logger.info('Location updated successfully:', variables.ids.locationId);
            queryClient.invalidateQueries({ queryKey: ['locations', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['location', variables.ids.storageId, variables.ids.locationId] });
            queryClient.invalidateQueries({ queryKey: ['products', variables.ids.storageId] });
        },
        onError: (error) => {
            logger.error('Failed to update location:', error);
        }
    });

    const deleteLocationMutation = useMutation({
        mutationFn: deleteLocationDoc,
        onSuccess: (result, variables) => {
            trackDelete('location');
            logger.info('Location deleted (soft) successfully:', variables.locationId);
            queryClient.invalidateQueries({ queryKey: ['locations', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['location', variables.storageId, variables.locationId] });
        },
        onError: (error) => {
            logger.error('Failed to delete location:', error);
        }
    });

    const restoreLocationMutation = useMutation({
        mutationFn: restoreLocationDoc,
        onSuccess: (result, variables) => {
            trackRestore('location');
            logger.info('Location restored successfully:', variables.locationId);
            queryClient.invalidateQueries({ queryKey: ['locations', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['location', variables.storageId, variables.locationId] });
        },
        onError: (error) => {
            logger.error('Failed to restore location:', error);
        }
    });

    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    const reloadLocations = () => {
        if (locationId) {
            refetchLocation();
        } else {
            refetchLocations();
        }
    };

    const findLocation = useMemo(() => {
        return (id: string): Location | undefined => {
            return allLocations?.find((loc: Location) => loc && loc.id === id);
        };
    }, [allLocations]);

    const getLocationNameString = useMemo(() => {
        return (id: string): string | undefined => {
            const item = findLocation(id);
            return item ? item.name : undefined;
        };
    }, [findLocation]);

    const getLocationNamesString = useMemo((locations: Location[] | undefined = allLocations) => {
        return (ids: string[]): string => {
            const names = ids
                .map(id => {
                    const item = locations?.find((loc: Location) => loc && loc.id === id);
                    return item ? item.name : null;
                })
                .filter(Boolean);
            return names.join(', ');
        };
    }, [allLocations]);

    const locationsInitialized = useMemo(() => {
        if (!storageId) return false;
        if (locationId) {
            return getStatus(locationLoading, locationError) === FormStatus.SUCCESS;
        }
        return getStatus(locationsLoading, locationsError) === FormStatus.SUCCESS;
    }, [storageId, locationId, locationLoading, locationError, locationsLoading, locationsError]);

    return {
        location: location || null,
        locationStatus: getStatus(locationLoading, locationError),
        
        locations: allLocations || [],
        allLocations: allLocations || [],
        filteredLocations,
        locationsStatus: getStatus(locationsLoading, locationsError),
        locationsInitialized,
        
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted,
        
        reloadLocations,
        
        createLocationAsync: createLocationMutation.mutateAsync,
        updateLocationAsync: updateLocationMutation.mutateAsync,
        deleteLocationAsync: deleteLocationMutation.mutateAsync,
        restoreLocationAsync: restoreLocationMutation.mutateAsync,
        isCreating: createLocationMutation.isPending,
        isUpdating: updateLocationMutation.isPending,
        isDeleting: deleteLocationMutation.isPending,
        isRestoring: restoreLocationMutation.isPending,
        
        findLocation,
        getLocationNameString,
        getLocationNamesString,
        
        refreshLocations: refetchLocations,
        refreshLocation: refetchLocation,
    };
};

export default useLocations;
